import {SeparatorCode, stringToNumber} from '@pb/kit-formatters';
import {keyCodes} from '@pb/kit-util-key-codes';

import {ValidType, MAX_INT_32_NUMBER} from './constants';
import {formatDateRange} from '../../data-typed-input/utils/formatDateRange';

import type {UserLocaleDto, ValidTypes} from './constants';

type ValueIsValid = {
    key: string,
    value: string,
    validateType: ValidTypes,
    cursorPositionStart: number,
    cursorPositionEnd: number,
    locale: UserLocaleDto,
};
const dataSeparatorsCode = [
    keyCodes.KEY_SUBTRACT,
    keyCodes.KEY_DIVIDE,
    keyCodes.KEY_SUBTRACT_IE,
] as const;
const manageButtons = [
    keyCodes.KEY_PAGE_UP,
    keyCodes.KEY_PAGE_DOWN,
    keyCodes.KEY_END,
    keyCodes.KEY_HOME,
    keyCodes.KEY_LEFT,
    keyCodes.KEY_UP,
    keyCodes.KEY_UP_IE,
    keyCodes.KEY_RIGHT,
    keyCodes.KEY_DOWN,
    keyCodes.KEY_DOWN_IE,
    keyCodes.KEY_DELETE,
    keyCodes.KEY_BACK_SPACE,
    keyCodes.KEY_ENTER,
    keyCodes.KEY_TAB,
] as const;

const isDigit = (key: string) => /^\d$/.test(key);

const additionalCharIsValid = (value: string, key: string, pos: number) =>
    value.charAt(pos - 1) !== key && value.charAt(pos) !== key;

const checkIsInt = ({
    key,
    value,
    separator,
    cursorPosition,
    isPositiveNumber,
    isInteger,
}: {
    key: string,
    value: string,
    separator: string,
    cursorPosition: number,
    isPositiveNumber: boolean,
    isInteger: boolean,
}) => {
    const isValidMinus =
        !isPositiveNumber &&
        key === keyCodes.KEY_SUBTRACT &&
        cursorPosition === 0 &&
        additionalCharIsValid(value, key, cursorPosition);

    if (key === '' && isInteger) {
        return Number.isInteger(Number(value));
    }

    return (
        isValidMinus ||
        (value.charAt(cursorPosition) !== keyCodes.KEY_SUBTRACT &&
            (key === separator
                ? value.charAt(cursorPosition - 1) !== keyCodes.KEY_SUBTRACT &&
                cursorPosition > 0 &&
                additionalCharIsValid(value, key, cursorPosition)
                : isDigit(key)))
    );
};

const checkAvailableSymbol = ({key, isPositiveNumber}: {key: string, isPositiveNumber: boolean}) =>
    (!isPositiveNumber && key === keyCodes.KEY_SUBTRACT) || isDigit(key);

const isFloat = (value: string, decimalSeparator: string, thousandSeparator: string) => {
    // 1. Remove all thousand separators
    const escapedThsSep = thousandSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const thsRegex = new RegExp(escapedThsSep, 'g');
    let cleaned = value.replace(thsRegex, '');

    // 2. Normalize decimal separator to '.'
    const escapedDecSep = decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const decRegex = new RegExp(escapedDecSep, 'g');
    cleaned = cleaned.replace(decRegex, '.');

    // 3. Check if it's a valid float: optional minus, digits, optional decimal point with digits
    const floatRegex = /^-?\d+(\.\d+)?$/;

    return floatRegex.test(cleaned) && !Number.isNaN(parseFloat(cleaned));
};

const isInt = ({
    key,
    value,
    separator,
    cursorPosition,
    isPositiveNumber,
    isInteger,
}: {
    key: string,
    value: string,
    separator: string,
    cursorPosition: number,
    isPositiveNumber: boolean,
    isInteger: boolean,
}) => {
    const {selectionStart, selectionEnd, value: activeValue} =
        document.activeElement as HTMLInputElement;
    const selection =
        activeValue && activeValue.slice(selectionStart ?? 0, selectionEnd ?? 0);
    const isValueHighlighted = selection === value && cursorPosition === 0;
    return isValueHighlighted
        ? checkAvailableSymbol({
                key,
                isPositiveNumber,
            })
        : checkIsInt({
                key,
                value,
                separator,
                cursorPosition,
                isPositiveNumber,
                isInteger,
            });
};

const isYear = (key: string, value: string, isReplacedValue: boolean) => {
    if ((value.length < 4 || isReplacedValue) && isDigit(key)) {
        return true;
    }

    if (key === '' && value.length <= 4) {
        return value.split('').every(isDigit);
    }

    return false;
};

const isNumber = ({
    key,
    value,
    decSep,
    thsSep,
    cursorPosition,
    isPositiveNumber,
}: {
    key: string,
    value: string,
    decSep: string,
    thsSep: string,
    cursorPosition: number,
    isPositiveNumber: boolean,
}) => {
    if (
        (key === decSep && value.includes(decSep)) ||
        (key === thsSep &&
            value.includes(decSep) &&
            value.indexOf(decSep) < cursorPosition)
    ) {
        return false;
    }

    return (
        key === decSep ||
        isInt({
            key,
            value,
            separator: thsSep,
            cursorPosition,
            isPositiveNumber,
            isInteger: true,
        }) || (key === '' && isFloat(value, decSep, thsSep))
    );
};

const isPhoneChar = (char: string) => /^[\d\s-+()/,.;]$/.test(char);

const isPhone = (key: string, value: string) => {
    if (key === '' && value.length) {
        return value.split('').every(isPhoneChar);
    }

    return isPhoneChar(key);
};

const isHttpPort = (key: string, value: string) => {
    if (key && value) {
        return isDigit(key) && [...value].every(isDigit) && Number(value) >= 0;
    }

    if (key) {
        return isDigit(key);
    }

    if (value) {
        return [...value].every(isDigit) && Number(value) >= 0;
    }

    return false;
};

const isChar = (key: string) => /[a-zA-Z]/.test(key);

export const valueIsValid = (params: ValueIsValid): boolean => {
    const {
        key,
        value,
        validateType,
        cursorPositionStart,
        cursorPositionEnd,
        locale,
    } = params;
    const {thousandFormat, decimalFormat, dateFormat} = locale;

    if (manageButtons.includes(key)) {
        return true;
    }

    const ths = SeparatorCode[thousandFormat as keyof typeof SeparatorCode] ?? SeparatorCode.SPACE;
    const dec = SeparatorCode[decimalFormat as keyof typeof SeparatorCode] ?? SeparatorCode.COMMA;

    switch (validateType) {
        case ValidType.INT:
        case ValidType.INT_POSITIVE:
            return isInt({
                key,
                value,
                separator: ths,
                cursorPosition: cursorPositionStart,
                isPositiveNumber: validateType === ValidType.INT_POSITIVE,
                isInteger: true,
            });

        case ValidType.INT_32:
            const number = stringToNumber(value, locale);
            return (
                isInt({
                    key,
                    value,
                    separator: ths,
                    cursorPosition: cursorPositionStart,
                    isPositiveNumber: true,
                    isInteger: true,
                }) &&
                number >= 0 &&
                number <= MAX_INT_32_NUMBER
            );

        case ValidType.YEAR:
            return isYear(
                key,
                value,
                cursorPositionStart !== cursorPositionEnd,
            );

        case ValidType.NUMBER:
        case ValidType.NUMBER_POSITIVE:
        case ValidType.MAX_SAFE_NUMBER:
        case ValidType.MAX_SAFE_NUMBER_POSITIVE:
            const isPositiveNumber =
                validateType === ValidType.NUMBER_POSITIVE ||
                validateType === ValidType.MAX_SAFE_NUMBER_POSITIVE;

            return isNumber({
                key,
                value,
                decSep: dec,
                thsSep: ths,
                cursorPosition: cursorPositionStart,
                isPositiveNumber,
            });

        case ValidType.NUMBER_0_TO_100:
            const numberValue = stringToNumber(value, locale);
            return (
                isNumber({
                    key,
                    value,
                    decSep: dec,
                    thsSep: ths,
                    cursorPosition: cursorPositionStart,
                    isPositiveNumber: validateType === ValidType.NUMBER_0_TO_100,
                }) &&
                numberValue >= 0 &&
                numberValue <= 100
            );

        case ValidType.INT_NUMBER_0_TO_100:
            const currentNumber = stringToNumber(value, locale);
            return (
                isInt({
                    key,
                    value,
                    separator: ths,
                    cursorPosition: cursorPositionStart,
                    isPositiveNumber: true,
                    isInteger: true,
                }) &&
                currentNumber >= 0 &&
                currentNumber <= 100
            );

        case ValidType.DATE:
            return isDigit(key) || dataSeparatorsCode.includes(key);

        case ValidType.DATE_RANGE:
            return (
                isDigit(key) ||
                dataSeparatorsCode.includes(key) ||
                isChar(key) ||
                !!formatDateRange(value, dateFormat)
            );

        case ValidType.FULL_DATE:
            return (
                isDigit(key) || dataSeparatorsCode.includes(key) || isChar(key)
            );

        case ValidType.PHONE:
            return isPhone(key, value);

        case ValidType.HTTP_PORT:
            return isHttpPort(key, value);
    }

    return false;
};
